import { z } from "zod";
import { VEHICLE_CLASSES, type VehicleClass } from "../x-atelier";

// =============================================================
// Module local « x-site » (PROTECTO) : le site public et sa réservation en
// ligne. Réglages (heures, acompte, coordonnées) côté back office ; contrat
// public (catalogue, disponibilités, devis, réservation, paiement Stripe,
// contact) consommé par le site statique `apps/site`.
// =============================================================

export type Weekday = "1" | "2" | "3" | "4" | "5" | "6" | "7";
export interface DayHours {
  open: string;
  close: string;
}

/** Plage horaire quotidienne « HH:MM » → « HH:MM » dans le fuseau de l'atelier. */
export interface TimeRange {
  start: string;
  end: string;
}

/**
 * Fermeture de l'atelier ou plage bloquée : des journées entières (`startTime`
 * absent) ou une plage horaire répétée chaque jour de `from` à `to` inclus.
 */
export interface Closure {
  id: string;
  from: string;
  /** Dernier jour inclus ; null = sans fin (plage récurrente). */
  to: string | null;
  startTime?: string | null;
  endTime?: string | null;
  /**
   * Jours de la semaine concernés (1 = lundi … 7 = dimanche) ; vide ou absent =
   * chaque jour de `from` à `to`. « Le vendredi à partir de midi » : `weekdays`
   * = ["5"], `startTime` = "12:00", `to` = null.
   */
  weekdays?: Weekday[] | null;
  /** Interne : jamais affiché sur le site, où les créneaux disparaissent simplement. */
  label: string;
}

/** Service (ou groupe de variantes) réservable en ligne hors du quota par demi-journée. */
export interface QuotaExemption {
  /** `g:<groupe de variantes>` ou `s:<service>`, comme les groupes du catalogue au CRM. */
  key: string;
  /** Nom affiché au CRM au moment du choix. */
  label: string;
  /** Réservations automatiques de ce service permises par demi-journée. */
  perHalfDay: number;
}

export interface SiteSettings {
  /** Réservation en ligne ouverte (sinon le site renvoie vers le téléphone). */
  onlineBooking: boolean;
  /** Agenda par employés, ou capacité globale avec affectation ultérieure. */
  schedulingMode?: "technicians" | "workshop";
  /** Heures d'ouverture par jour (1 = lundi … 7 = dimanche) ; null = fermé. */
  hours: Record<Weekday, DayHours | null>;
  /**
   * Pause de midi, indisponibilité interne quotidienne (null = aucune). Un
   * créneau proposé automatiquement ne la chevauche jamais : finir à l'heure
   * de début ou commencer à l'heure de fin reste permis ; seul un soin long
   * (`longServiceMinutes`) l'enjambe, le travail s'interrompant pendant la pause.
   */
  lunchBreak?: TimeRange | null;
  /** Fermetures exceptionnelles et plages bloquées (ponctuelles ou récurrentes). */
  closures?: Closure[];
  /**
   * Jours fériés du Québec (`QUEBEC_HOLIDAYS`) : fermé ou ouvert par clé. Une
   * clé absente garde la valeur par défaut du férié (`defaultClosed`).
   */
  holidays?: Record<string, boolean>;
  /**
   * Soins longs : au-delà de tant de minutes de travail, un soin commencé le
   * matin peut traverser la pause de midi ; le travail s'interrompt pendant la
   * pause et la fin est repoussée d'autant (null = jamais).
   */
  longServiceMinutes?: number | null;
  /**
   * Réservations automatiques (site, assistant du site, Léo) : au plus tant
   * par demi-journée, en plus des présences et de la capacité (null = sans
   * limite propre). Les employés au CRM n'y sont pas soumis.
   */
  onlineMorningMax?: number | null;
  onlineAfternoonMax?: number | null;
  /** Heures de début « HH:MM » offertes aux réservations automatiques ; vide = à chaque pas. */
  onlineStartTimes?: string[];
  /**
   * Services hors quota : une réservation dont tous les services sont ici se
   * prend à toute heure du pas, jusqu'au nombre propre de chacun par demi-journée.
   */
  quotaExemptions?: QuotaExemption[];
  /** Pas des créneaux proposés, en minutes. */
  slotStepMin: number;
  /** Délai minimal avant un créneau, en heures. */
  leadTimeHours: number;
  /** Capacité simultanée de l’atelier ; null = limitée par les techniciens. */
  maxConcurrentAppointments: number | null;
  /** Horizon de réservation, en jours. */
  maxDaysAhead: number;
  /** Acompte demandé à la réservation : pourcentage du total TTC, plancher en cents. */
  depositPercent: number;
  depositMinCents: number;
  /** Coordonnées affichées sur le site. */
  address: string;
  city: string;
  postalCode: string;
  phone: string;
  email: string;
  mapsUrl: string;
  instagramUrl: string;
  facebookUrl: string;
  /** Phrase affichée dans le parcours de réservation. */
  bookingNote: string;
}

/** Ce que le site public lit au chargement. */
export interface SiteConfig {
  name: string;
  tagline: string;
  onlineBooking: boolean;
  hours: Record<Weekday, DayHours | null>;
  slotStepMin: number;
  leadTimeHours: number;
  maxDaysAhead: number;
  depositPercent: number;
  depositMinCents: number;
  address: string;
  city: string;
  postalCode: string;
  phone: string;
  email: string;
  mapsUrl: string;
  instagramUrl: string;
  facebookUrl: string;
  bookingNote: string;
  /** Paiement en ligne configuré ; sinon paiement à l’atelier. */
  stripeConfigured: boolean;
}

/** Liaison vue du site et de Léo : service lié, obligatoire ou recommandé, note dite au client. */
export interface SiteServiceLink {
  linkedServiceId: string;
  kind: "obligatoire" | "recommande";
  note: string | null;
  vehicleClasses: VehicleClass[];
}

export interface SiteService {
  vehicleClasses: VehicleClass[];
  variantGroup: string | null;
  id: string;
  name: string;
  description: string | null;
  /** Prix hors taxes ; null = sur devis. Au tarif horaire : le taux d'une heure. */
  priceCents: number | null;
  /** « hourly » : montant final selon le temps requis. */
  pricingUnit: "fixed" | "hourly";
  durationMin: number;
  /** Forfait : services regroupés. */
  bundleServiceIds: string[];
  /** Réservé aux véhicules de tant d'années ou moins ; null = sans limite. */
  maxVehicleAgeYears: number | null;
  /** Transport offert dans ce rayon (km) ; null = pas un transport. */
  maxDistanceKm: number | null;
  links: SiteServiceLink[];
}

export interface SiteCategory {
  id: string;
  name: string;
  services: SiteService[];
}

export interface QuoteLine {
  quantity: number;
  serviceId: string;
  label: string;
  priceCents: number;
  quoteOnly: boolean;
  durationMin: number;
  discountPct: number;
  discountLabel: string | null;
  totalCents: number;
  /** Tarif horaire : `priceCents` est le taux d'une heure, `quantity` les heures estimées. */
  pricingUnit?: "fixed" | "hourly";
  hourlyRateCents?: number | null;
  /** Ligne ajoutée d'office par une liaison obligatoire (depuis quel service). */
  requiredBy?: string | null;
}

export interface QuoteRecommendation {
  serviceId: string;
  name: string;
  /** Service qui recommande celui-ci. */
  fromServiceId: string;
  fromName: string;
  note: string | null;
}

export interface Quote {
  lines: QuoteLine[];
  subtotalCents: number;
  gstCents: number;
  qstCents: number;
  totalWithTaxCents: number;
  depositCents: number;
  durationMin: number;
  /** Un forfait remplace avantageusement les services choisis. */
  bundle: { id: string; name: string; priceCents: number; savingsCents: number } | null;
  /** Au moins une ligne au tarif horaire : le total est une estimation selon le temps requis. */
  estimated?: boolean;
  /** Services recommandés par la sélection, pas encore choisis. */
  recommended?: QuoteRecommendation[];
  /** Un service de la sélection est réservé aux véhicules plus récents que celui donné. */
  vehicleAgeLimit?: {
    serviceId: string;
    serviceName: string;
    maxYears: number;
    vehicleYear: number;
  } | null;
}

export type DayStatus = "disponible" | "complet" | "ferme" | "passe";

export interface AvailabilityDay {
  date: string;
  status: DayStatus;
  freeSlots: number;
  technicians: number;
}

export interface Slot {
  startAt: string;
  /** Fin prévue : début + durée, pause de midi comprise si un soin long la traverse. */
  endAt: string;
  label: string;
  technicianIds: string[];
}

export type BookingPayment = "acompte" | "sur_place";

export interface BookingResult {
  id: string;
  token: string;
  appointmentId: string;
  title: string;
  startAt: string;
  endAt: string;
  totalWithTaxCents: number;
  depositCents: number;
  payment: BookingPayment;
  /** Page de paiement Stripe ; null si paiement sur place ou Stripe absent. */
  checkoutUrl: string | null;
  /** Indicateur conservé pour les anciennes réservations de démonstration. */
  simulated: boolean;
}

export interface BookingStatus {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
  status: string;
  /** Véhicule tel qu'il est enregistré (nom officiel du modèle). */
  vehicleLabel: string | null;
  /** Services réservés, dans l'ordre du rendez-vous. */
  services: string[];
  payment: BookingPayment;
  depositCents: number;
  paidCents: number;
  paidAt: string | null;
  simulated: boolean;
}

// ---- Libellés ----

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  "1": "Lundi",
  "2": "Mardi",
  "3": "Mercredi",
  "4": "Jeudi",
  "5": "Vendredi",
  "6": "Samedi",
  "7": "Dimanche",
};

// ---- Schémas ----

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "heure HH:MM attendue");
const dayHoursSchema = z
  .object({ open: hhmm, close: hhmm })
  .refine((v) => v.close > v.open, "La fermeture doit suivre l’ouverture")
  .nullable();

export const DEFAULT_HOURS: Record<Weekday, DayHours | null> = {
  "1": { open: "08:00", close: "17:00" },
  "2": { open: "08:00", close: "17:00" },
  "3": { open: "08:00", close: "17:00" },
  "4": { open: "08:00", close: "17:00" },
  "5": { open: "08:00", close: "17:00" },
  "6": null,
  "7": null,
};

const url = z.union([z.string().trim().url(), z.literal("")]);

/** Pause de midi par défaut : de 12 h à 13 h, chaque jour ouvert. */
export const DEFAULT_LUNCH_BREAK: TimeRange = { start: "12:00", end: "13:00" };

export const timeRangeSchema = z
  .object({ start: hhmm, end: hhmm })
  .refine((v) => v.end > v.start, "La fin doit suivre le début");

const dayKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date AAAA-MM-JJ attendue");

export const closureSchema = z
  .object({
    id: z.string().trim().min(1).max(40),
    from: dayKey,
    to: dayKey.nullable().default(null),
    startTime: hhmm.nullable().optional(),
    endTime: hhmm.nullable().optional(),
    weekdays: z
      .array(z.enum(["1", "2", "3", "4", "5", "6", "7"]))
      .max(7)
      .nullable()
      .optional(),
    label: z.string().trim().max(120).default(""),
  })
  .refine((v) => v.to === null || v.to >= v.from, "La fin doit suivre le début")
  .refine(
    (v) =>
      Boolean(v.startTime) === Boolean(v.endTime) && (!v.startTime || v.endTime! > v.startTime),
    "Plage horaire incomplète",
  );

/** Heures de début offertes par défaut aux réservations automatiques : 8 h ou 9 h, 13 h ou 14 h. */
export const DEFAULT_ONLINE_START_TIMES = ["08:00", "09:00", "13:00", "14:00"];

export const quotaExemptionSchema = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[gs]:.{1,120}$/, "clé de service attendue"),
  label: z.string().trim().max(160).default(""),
  perHalfDay: z.number().int().min(1).max(50),
});

export const settingsSchema = z.object({
  onlineBooking: z.boolean().default(false),
  schedulingMode: z.enum(["technicians", "workshop"]).default("technicians"),
  hours: z
    .object({
      "1": dayHoursSchema,
      "2": dayHoursSchema,
      "3": dayHoursSchema,
      "4": dayHoursSchema,
      "5": dayHoursSchema,
      "6": dayHoursSchema,
      "7": dayHoursSchema,
    })
    .default(DEFAULT_HOURS),
  lunchBreak: timeRangeSchema.nullable().default(DEFAULT_LUNCH_BREAK),
  closures: z.array(closureSchema).max(200).default([]),
  holidays: z.record(z.string().max(60), z.boolean()).default({}),
  longServiceMinutes: z
    .number()
    .int()
    .min(30)
    .max(24 * 60)
    .nullable()
    .default(240),
  onlineMorningMax: z.number().int().min(0).max(50).nullable().default(1),
  onlineAfternoonMax: z.number().int().min(0).max(50).nullable().default(1),
  onlineStartTimes: z
    .array(hhmm)
    .max(48)
    .transform((times) => [...new Set(times)].sort())
    .default(DEFAULT_ONLINE_START_TIMES),
  quotaExemptions: z
    .array(quotaExemptionSchema)
    .max(200)
    .transform((list) => [...new Map(list.map((e) => [e.key, e])).values()])
    .default([]),
  slotStepMin: z.number().int().min(15).max(120).default(30),
  leadTimeHours: z.number().int().min(0).max(240).default(24),
  maxConcurrentAppointments: z.number().int().min(1).max(100).nullable().default(null),
  maxDaysAhead: z.number().int().min(7).max(365).default(60),
  depositPercent: z.number().int().min(0).max(100).default(25),
  depositMinCents: z.number().int().min(0).default(5000),
  address: z.string().trim().max(200).default("2147 boulevard Talbot"),
  city: z.string().trim().max(120).default("Chicoutimi"),
  postalCode: z.string().trim().max(20).default("G7H 8B3"),
  phone: z.string().trim().max(40).default("+14186023874"),
  email: z.union([z.string().trim().email(), z.literal("")]).default("sachacns2147@hotmail.com"),
  mapsUrl: url.default(""),
  instagramUrl: url.default(""),
  facebookUrl: url.default(""),
  bookingNote: z
    .string()
    .max(400)
    .default(
      "Un acompte réserve votre place ; le solde se règle à l'atelier, à la remise du véhicule.",
    ),
});
export const updateSettingsSchema = settingsSchema.partial();
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

export const dayKeySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "date AAAA-MM-JJ attendue")
  .refine((v) => {
    const d = new Date(`${v}T12:00:00Z`);
    return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, "Date invalide");

export const quoteSchema = z.object({
  vehicleClass: z.enum(VEHICLE_CLASSES).optional(),
  /** Année du véhicule, pour les services réservés aux véhicules récents. */
  vehicleYear: z.number().int().min(1950).max(2100).nullable().optional(),
  serviceIds: z.array(z.string().min(1)).min(1).max(20),
  quantities: z.record(z.string().min(1), z.number().int().min(1).max(8)).optional(),
});

/** « id1,id2 » : les services choisis, pour appliquer leurs exemptions de quota. */
const serviceIdsParam = z
  .string()
  .max(2000)
  .optional()
  .transform((v) =>
    v
      ? [
          ...new Set(
            v
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean),
          ),
        ].slice(0, 20)
      : undefined,
  );

export const availabilityQuerySchema = z.object({
  from: dayKeySchema,
  to: dayKeySchema,
  durationMin: z.coerce
    .number()
    .int()
    .min(15)
    .max(24 * 60)
    .default(60),
  serviceIds: serviceIdsParam,
});

export const slotsQuerySchema = z.object({
  date: dayKeySchema,
  durationMin: z.coerce
    .number()
    .int()
    .min(15)
    .max(24 * 60)
    .default(60),
  serviceIds: serviceIdsParam,
});

export const bookingSchema = z.object({
  serviceIds: z.array(z.string().min(1)).min(1).max(20),
  quantities: z.record(z.string().min(1), z.number().int().min(1).max(8)).optional(),
  startAt: z.string().datetime({ offset: true }),
  client: z.object({
    name: z.string().trim().min(2).max(160),
    phone: z.string().trim().min(7).max(40),
    email: z.union([z.string().trim().email(), z.literal("")]).optional(),
    smsConsent: z.boolean().default(false),
  }),
  vehicle: z.object({
    pricingClass: z.enum(VEHICLE_CLASSES).optional(),
    make: z.string().trim().min(1).max(80),
    model: z.string().trim().min(1).max(80),
    year: z.number().int().min(1950).max(2100).nullable().optional(),
    plate: z.string().trim().max(16).nullable().optional(),
  }),
  notes: z.string().max(2000).optional(),
  payment: z.enum(["acompte", "sur_place"]).default("acompte"),
  /** Langue de la page qui réserve : décide de la page de retour (« merci » / « thank-you »). */
  lang: z.enum(["fr", "en"]).optional(),
  /**
   * Clé tirée au hasard par la page à chaque tentative : un double clic ou un
   * renvoi réseau retrouve la même réservation au lieu d'en créer une seconde.
   */
  requestKey: z
    .string()
    .regex(/^[A-Za-z0-9_-]{8,64}$/)
    .optional(),
});
export type BookingInput = z.infer<typeof bookingSchema>;

export const contactSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    phone: z.string().trim().max(40).optional(),
    email: z.union([z.string().trim().email(), z.literal("")]).optional(),
    message: z.string().trim().min(5).max(6000),
    /** « reservation » : demande de rendez-vous sans créneau (soin sur devis, réservation fermée). */
    kind: z.enum(["reservation", "contact"]).optional(),
    details: z.lazy(() => siteRequestDetailsSchema).optional(),
  })
  .refine(
    (v) => Boolean(v.email || (v.phone && v.phone.replace(/\D/g, "").length >= 7)),
    "Un téléphone ou courriel est nécessaire",
  );
export type ContactInput = z.infer<typeof contactSchema>;

// ---- Fuseau de l'atelier, pause de midi et fermetures ----

/** Fuseau de l'atelier (Saguenay) : heures d'ouverture, créneaux et pauses s'y lisent, changements d'heure compris. */
export const SHOP_TIME_ZONE = "America/Toronto";

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const zonedFormatters = new Map<string, Intl.DateTimeFormat>();

function zonedParts(ms: number, zone: string): ZonedParts {
  let fmt = zonedFormatters.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    zonedFormatters.set(zone, fmt);
  }
  const parts = Object.fromEntries(fmt.formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  };
}

/** Jour « AAAA-MM-JJ » d'un instant, dans le fuseau donné. */
export function zonedDayKey(ms: number, zone = SHOP_TIME_ZONE): string {
  const p = zonedParts(ms, zone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Instant (ms) d'une heure murale « HH:MM » d'un jour donné dans le fuseau, changements d'heure compris. */
export function zonedTimeToMs(date: string, hhmm: string, zone = SHOP_TIME_ZONE): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [h, mi] = hhmm.split(":").map(Number) as [number, number];
  const wanted = Date.UTC(y, m - 1, d, h, mi);
  let guess = wanted;
  // Deux corrections suffisent : l'écart entre l'heure murale et UTC change au plus une fois.
  for (let i = 0; i < 2; i++) {
    const p = zonedParts(guess, zone);
    guess += wanted - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  }
  return guess;
}

function addDays(date: string, days: number): string {
  const t = new Date(`${date}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

export interface InternalBlock {
  kind: "pause" | "fermeture";
  start: number;
  end: number;
  /** Interne (affiché au CRM, jamais sur le site). */
  label: string;
}

// ---- Jours fériés du Québec ----

export interface HolidayDefinition {
  key: string;
  label: string;
  /** Fermé si rien n'est réglé ; les autres se cochent dans les réglages du site. */
  defaultClosed: boolean;
  /** Date « AAAA-MM-JJ » du férié pour une année donnée. */
  date: (year: number) => string;
}

const isoDay = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Jour de la semaine d'une date civile (1 = lundi … 7 = dimanche), sans fuseau. */
export function weekdayOf(date: string): Weekday {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return String(((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1) as Weekday;
}

/** Dimanche de Pâques (calendrier grégorien, algorithme de Meeus/Jones/Butcher). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDay(year, month, day);
}

/** `n`-ième jour de semaine (1 = lundi … 7 = dimanche) d'un mois : « 2e lundi d'octobre ». */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): string {
  const first = isoDay(year, month, 1);
  const offset = (weekday - Number(weekdayOf(first)) + 7) % 7;
  return addDays(first, offset + (n - 1) * 7);
}

/** Dernier lundi tombant au plus tard à la date donnée : « le lundi qui précède le 25 mai ». */
function mondayOnOrBefore(year: number, month: number, day: number): string {
  const date = isoDay(year, month, day);
  return addDays(date, -((Number(weekdayOf(date)) - 1) % 7));
}

/**
 * Jours fériés du Québec, préremplis : les huit fériés de la Loi sur les normes
 * du travail (Vendredi saint et lundi de Pâques tous deux proposés, l'employeur
 * choisit), plus les fermetures d'usage (lendemain de Noël) et les veilles,
 * ouvertes par défaut. Chaque férié se désactive dans les réglages du site.
 */
export const QUEBEC_HOLIDAYS: HolidayDefinition[] = [
  { key: "jour-de-l-an", label: "Jour de l’An", defaultClosed: true, date: (y) => isoDay(y, 1, 1) },
  {
    key: "lendemain-du-jour-de-l-an",
    label: "Lendemain du jour de l’An",
    defaultClosed: false,
    date: (y) => isoDay(y, 1, 2),
  },
  {
    key: "vendredi-saint",
    label: "Vendredi saint",
    defaultClosed: true,
    date: (y) => addDays(easterSunday(y), -2),
  },
  {
    key: "lundi-de-paques",
    label: "Lundi de Pâques",
    defaultClosed: true,
    date: (y) => addDays(easterSunday(y), 1),
  },
  {
    key: "patriotes",
    label: "Journée nationale des patriotes",
    defaultClosed: true,
    date: (y) => mondayOnOrBefore(y, 5, 24),
  },
  {
    key: "fete-nationale",
    label: "Fête nationale du Québec",
    defaultClosed: true,
    date: (y) => isoDay(y, 6, 24),
  },
  {
    key: "fete-du-canada",
    label: "Fête du Canada",
    defaultClosed: true,
    date: (y) => isoDay(y, 7, 1),
  },
  {
    key: "fete-du-travail",
    label: "Fête du Travail",
    defaultClosed: true,
    date: (y) => nthWeekdayOfMonth(y, 9, 1, 1),
  },
  {
    key: "action-de-grace",
    label: "Action de grâce",
    defaultClosed: true,
    date: (y) => nthWeekdayOfMonth(y, 10, 1, 2),
  },
  {
    key: "veille-de-noel",
    label: "Veille de Noël",
    defaultClosed: false,
    date: (y) => isoDay(y, 12, 24),
  },
  { key: "noel", label: "Noël", defaultClosed: true, date: (y) => isoDay(y, 12, 25) },
  {
    key: "lendemain-de-noel",
    label: "Lendemain de Noël",
    defaultClosed: true,
    date: (y) => isoDay(y, 12, 26),
  },
  {
    key: "veille-du-jour-de-l-an",
    label: "Veille du jour de l’An",
    defaultClosed: false,
    date: (y) => isoDay(y, 12, 31),
  },
];

/** L'atelier ferme-t-il pour ce férié ? Réglage de l'atelier, sinon la valeur par défaut. */
export function holidayClosed(
  holiday: HolidayDefinition,
  holidays: Record<string, boolean> | undefined,
): boolean {
  return holidays?.[holiday.key] ?? holiday.defaultClosed;
}

/** Les fériés d'une année, datés, avec leur état (fermé ou ouvert) pour cet atelier. */
export function holidaysForYear(
  year: number,
  holidays?: Record<string, boolean>,
): Array<{ key: string; label: string; date: string; closed: boolean }> {
  return QUEBEC_HOLIDAYS.map((h) => ({
    key: h.key,
    label: h.label,
    date: h.date(year),
    closed: holidayClosed(h, holidays),
  })).sort((a, b) => a.date.localeCompare(b.date));
}

/** Le férié fermé qui tombe ce jour-là, s'il y en a un. */
export function holidayOn(
  date: string,
  holidays: Record<string, boolean> | undefined,
): HolidayDefinition | null {
  const year = Number(date.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  return QUEBEC_HOLIDAYS.find((h) => h.date(year) === date && holidayClosed(h, holidays)) ?? null;
}

/** Une fermeture s'applique-t-elle ce jour-là (bornes, et jours de semaine s'ils sont réglés) ? */
export function closureAppliesOn(c: Closure, date: string): boolean {
  if (date < c.from || (c.to !== null && c.to !== undefined && date > c.to)) return false;
  if (c.weekdays && c.weekdays.length > 0 && !c.weekdays.includes(weekdayOf(date))) return false;
  return true;
}

/** Règles d'indisponibilité internes lues par le site, Léo et le CRM. */
export type InternalBlockRules = Pick<SiteSettings, "lunchBreak" | "closures" | "holidays">;

/**
 * Indisponibilités internes d'une journée : la pause de midi (si l'atelier est
 * ouvert ce jour-là ou si `openOnly` est faux), le férié fermé et les
 * fermetures (ponctuelles ou récurrentes) qui la touchent.
 */
export function internalBlocksForDay(
  date: string,
  rules: InternalBlockRules,
  zone = SHOP_TIME_ZONE,
): InternalBlock[] {
  const blocks: InternalBlock[] = [];
  if (rules.lunchBreak) {
    blocks.push({
      kind: "pause",
      start: zonedTimeToMs(date, rules.lunchBreak.start, zone),
      end: zonedTimeToMs(date, rules.lunchBreak.end, zone),
      label: "Pause de midi",
    });
  }
  const holiday = holidayOn(date, rules.holidays);
  if (holiday) {
    blocks.push({
      kind: "fermeture",
      start: zonedTimeToMs(date, "00:00", zone),
      end: zonedTimeToMs(addDays(date, 1), "00:00", zone),
      label: holiday.label,
    });
  }
  for (const c of rules.closures ?? []) {
    if (!closureAppliesOn(c, date)) continue;
    const allDay = !c.startTime || !c.endTime;
    blocks.push({
      kind: "fermeture",
      start: zonedTimeToMs(date, allDay ? "00:00" : c.startTime!, zone),
      end: allDay
        ? zonedTimeToMs(addDays(date, 1), "00:00", zone)
        : zonedTimeToMs(date, c.endTime!, zone),
      label: c.label || "Fermeture",
    });
  }
  return blocks;
}

/** Un soin de tant de minutes de travail peut-il traverser la pause de midi ? */
export function isLongService(
  workMinutes: number,
  rules: Pick<SiteSettings, "longServiceMinutes">,
): boolean {
  return rules.longServiceMinutes != null && workMinutes > rules.longServiceMinutes;
}

/**
 * Fin d'un soin de `workMinutes` minutes commencé à `startMs`. Un soin long
 * commencé avant la pause de midi et qui la dépasserait s'interrompt pendant la
 * pause : sa fin est repoussée de la durée de la pause (8 h + 5 h → 14 h). Les
 * autres finissent à début + durée ; un chevauchement est alors contrôlé à part.
 */
export function serviceEndMs(
  startMs: number,
  workMinutes: number,
  rules: Pick<SiteSettings, "lunchBreak" | "longServiceMinutes">,
  zone = SHOP_TIME_ZONE,
): number {
  const end = startMs + workMinutes * 60_000;
  if (!rules.lunchBreak || !isLongService(workMinutes, rules)) return end;
  const day = zonedDayKey(startMs, zone);
  const lunchStart = zonedTimeToMs(day, rules.lunchBreak.start, zone);
  const lunchEnd = zonedTimeToMs(day, rules.lunchBreak.end, zone);
  return startMs < lunchStart && end > lunchStart ? end + (lunchEnd - lunchStart) : end;
}

/**
 * Indisponibilités internes que [start, end[ chevauche. Finir exactement au
 * début d'une pause ou commencer exactement à sa fin ne la chevauche pas. Avec
 * `workMinutes`, un soin long qui enjambe toute la pause (commencé avant, fini
 * après) ne la chevauche pas non plus : le travail s'y interrompt.
 */
export function internalBlocksOverlapping(
  startMs: number,
  endMs: number,
  rules: InternalBlockRules & Pick<SiteSettings, "longServiceMinutes">,
  zone = SHOP_TIME_ZONE,
  workMinutes?: number,
): InternalBlock[] {
  if (!(endMs > startMs)) return [];
  const long = workMinutes !== undefined && isLongService(workMinutes, rules);
  const out: InternalBlock[] = [];
  const last = zonedDayKey(endMs - 1, zone);
  for (let day = zonedDayKey(startMs, zone); day <= last; day = addDays(day, 1)) {
    for (const b of internalBlocksForDay(day, rules, zone)) {
      if (!(b.start < endMs && b.end > startMs)) continue;
      if (long && b.kind === "pause" && startMs < b.start && endMs >= b.end) continue;
      out.push(b);
    }
  }
  return out;
}

/** Clés sous lesquelles un service peut être exempté : son groupe de variantes, puis lui-même. */
export function serviceQuotaKeys(s: { id: string; variantGroup?: string | null }): string[] {
  return s.variantGroup ? [`g:${s.variantGroup}`, `s:${s.id}`] : [`s:${s.id}`];
}

/** L'exemption de quota qui couvre ce service, s'il y en a une. */
export function quotaExemptionFor(
  s: { id: string; variantGroup?: string | null },
  exemptions: QuotaExemption[] | undefined,
): QuotaExemption | null {
  if (!exemptions?.length) return null;
  const keys = serviceQuotaKeys(s);
  return exemptions.find((e) => keys.includes(e.key)) ?? null;
}

/** Règles d'agenda lues par le CRM (sélecteur de créneaux, avertissements). */
export interface AgendaRules {
  hours: Record<Weekday, DayHours | null>;
  slotStepMin: number;
  lunchBreak: TimeRange | null;
  closures: Closure[];
  /** Fériés fermés ou ouverts, par clé de `QUEBEC_HOLIDAYS`. */
  holidays: Record<string, boolean>;
  /** Au-delà, un soin traverse la pause de midi (fin repoussée d'autant) ; null = jamais. */
  longServiceMinutes: number | null;
  schedulingMode: "technicians" | "workshop";
  maxConcurrentAppointments: number | null;
  timeZone: string;
}

// ---- Demandes reçues du site ----

/** « nouveau » = à traiter ; « archive » dégage la liste active sans rien supprimer. */
export type SiteRequestStatus = "nouveau" | "en_cours" | "traite" | "archive";
export const SITE_REQUEST_STATUSES: SiteRequestStatus[] = [
  "nouveau",
  "en_cours",
  "traite",
  "archive",
];
export const SITE_REQUEST_STATUS_LABELS: Record<SiteRequestStatus, string> = {
  nouveau: "À traiter",
  en_cours: "En cours",
  traite: "Traitée",
  archive: "Archivée",
};
/** Réservation demandée (parcours sans créneau, ancienne demande) ou message général. */
export type SiteRequestKind = "reservation" | "contact";

/** Ce que le site transmet en plus du message, pour préremplir un rendez-vous sans ressaisie. */
export const siteRequestDetailsSchema = z.object({
  vehicle: z
    .object({
      pricingClass: z.enum(VEHICLE_CLASSES).nullable().optional(),
      make: z.string().trim().max(80).nullable().optional(),
      model: z.string().trim().max(80).nullable().optional(),
      year: z.number().int().min(1950).max(2100).nullable().optional(),
      plate: z.string().trim().max(16).nullable().optional(),
    })
    .optional(),
  serviceIds: z.array(z.string().min(1)).max(20).optional(),
  quantities: z.record(z.string().min(1), z.number().int().min(1).max(8)).optional(),
  /** Préférence de date ou d'heure, en mots du client. */
  preference: z.string().trim().max(180).nullable().optional(),
  /** Jour souhaité (« AAAA-MM-JJ »), choisi parmi les jours d'ouverture ; absent = dès que possible. */
  preferredDate: dayKey.nullable().optional(),
  /** Moment souhaité : le matin ou l'après-midi ; absent = peu importe. */
  preferredPeriod: z.enum(["matin", "apres-midi"]).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  smsConsent: z.boolean().optional(),
  lang: z.enum(["fr", "en"]).optional(),
});
export type SiteRequestDetails = z.infer<typeof siteRequestDetailsSchema>;

export interface SiteRequest {
  id: string;
  kind: SiteRequestKind;
  name: string;
  phone: string | null;
  email: string | null;
  message: string;
  status: SiteRequestStatus;
  assignedTo: string | null;
  assigneeName: string | null;
  clientId: string | null;
  clientName: string | null;
  /** Rendez-vous créé depuis la demande (null s'il n'existe plus ou n'a jamais existé). */
  appointmentId: string | null;
  appointmentStartAt: string | null;
  appointmentStatus: string | null;
  details: SiteRequestDetails | null;
  createdAt: string;
  updatedAt: string;
  handledAt: string | null;
  archivedAt: string | null;
}

export interface SiteRequestEvent {
  id: string;
  description: string;
  actorName: string | null;
  createdAt: string;
}

export const requestListQuerySchema = z.object({
  statut: z
    .enum(["a_traiter", "en_cours", "traitees", "archivees", "actives", "toutes"])
    .default("actives"),
  q: z.string().trim().max(120).optional(),
});

export const updateRequestSchema = z.object({
  status: z.enum(["nouveau", "en_cours", "traite", "archive"]).optional(),
  assignedTo: z.string().nullable().optional(),
  note: z.string().trim().min(1).max(2000).optional(),
});

/** Préremplissage d'un rendez-vous depuis une demande (client, véhicule, services, préférence). */
export interface SiteRequestPrefill {
  requestId: string;
  client: {
    /** Fiche existante retrouvée par téléphone ou courriel ; null = à créer. */
    id: string | null;
    name: string;
    phone: string | null;
    email: string | null;
  };
  vehicle: {
    /** Véhicule déjà au dossier du client ; null = à créer avec ces renseignements. */
    id: string | null;
    pricingClass: VehicleClass | null;
    make: string | null;
    model: string | null;
    year: number | null;
    plate: string | null;
  } | null;
  lines: Array<{ serviceId: string; quantity: number; label: string }>;
  /** Services cités dans une ancienne demande mais introuvables au catalogue. */
  unmatchedServices: string[];
  preference: string | null;
  /** Jour et moment choisis par le client (demande du site), pour ouvrir le sélecteur de créneaux. */
  preferredDate: string | null;
  preferredPeriod: "matin" | "apres-midi" | null;
  notes: string | null;
  smsConsent: boolean | null;
}
